import api from './api';

/** Filter DevTools console by `areasApi` (Verbose level in Chrome). */
function debugAreasApi(phase: string, detail?: Record<string, unknown>) {
  console.debug('[areasApi]', phase, detail ?? '');
}

export type AreaMapShape = 'oval' | 'rectangle';
export type AreaPresetType = 'user' | 'light' | 'hidden' | 'system';

/** All valid area/preset types (DB enum order). */
export const AREA_PRESET_TYPES: readonly AreaPresetType[] = [
  'user',
  'light',
  'system',
  'hidden',
] as const;

const AREA_PRESET_TYPE_LABELS: Record<AreaPresetType, string> = {
  user: 'User',
  light: 'Light',
  system: 'System',
  hidden: 'Hidden',
};

export function getAreaPresetTypeLabel(type: AreaPresetType): string {
  return AREA_PRESET_TYPE_LABELS[type] ?? type;
}

export interface Area {
  id: string;
  siteControllerId: string;
  type: AreaPresetType;
  name: string;
  address: number | null;
  currentPresetId: string | null;
  /** Local HH:MM (24h). Present for super_user and site_admin only. */
  curfewStart?: string | null;
  curfewEnd?: string | null;
  /** Site map placement (optional). */
  latitude?: number | null;
  longitude?: number | null;
  shape?: AreaMapShape | null;
  widthM?: number | null;
  heightM?: number | null;
  orientationDegrees?: number | null;
  createdAt: string;
  updatedAt: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AreaResponse extends Area {
}

export interface SetCurrentPresetResponse extends AreaResponse {
  warning?: string;
}

export interface CreateAreaData {
  siteControllerId: string;
  name: string;
  address?: number; // Device address: 0-255
  type?: AreaPresetType;
  currentPresetId?: string | null;
  curfewStart?: string | null;
  curfewEnd?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  shape?: AreaMapShape | null;
  widthM?: number | null;
  heightM?: number | null;
  orientationDegrees?: number | null;
}

export interface UpdateAreaData {
  name?: string;
  address?: number; // Device address: 0-255
  type?: AreaPresetType;
  currentPresetId?: string | null;
  curfewStart?: string | null;
  curfewEnd?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  shape?: AreaMapShape | null;
  widthM?: number | null;
  heightM?: number | null;
  orientationDegrees?: number | null;
}

export interface AreaFilter {
  includeDeleted?: boolean;
  siteControllerId?: string;
  address?: number;
  name?: string;
  type?: AreaPresetType;
}

export const areasApi = {
  getBySiteController: async (siteControllerId: string, type?: AreaPresetType): Promise<AreaResponse[]> => {
    const params: { siteControllerId: string; type?: AreaPresetType } = { siteControllerId };
    if (type) {
      params.type = type;
    }
    const { data } = await api.get('/areas', { params });
    const areas = data as AreaResponse[];
    // Explicit type requests (including hidden/system) are returned as-is; unfiltered lists hide them.
    return areas
  },

  getWithFilter: async (filter: AreaFilter): Promise<AreaResponse[]> => {
    const { data } = await api.get('/areas', { params: filter });
    const areas = data as AreaResponse[];
    return areas
  },

  getById: async (id: string): Promise<AreaResponse> => {
    const { data } = await api.get(`/areas/${id}`);
    return data;
  },

  create: async (payload: CreateAreaData): Promise<AreaResponse> => {
    const { data } = await api.post('/areas', payload);
    return data;
  },

  update: async (id: string, payload: UpdateAreaData): Promise<AreaResponse> => {
    const mapKeys: (keyof UpdateAreaData)[] = [
      'latitude',
      'longitude',
      'shape',
      'widthM',
      'heightM',
      'orientationDegrees',
    ];
    const mapPatch: Partial<Pick<UpdateAreaData, (typeof mapKeys)[number]>> = {};
    for (const k of mapKeys) {
      if (k in payload && payload[k] !== undefined) {
        (mapPatch as Record<string, unknown>)[k] = payload[k];
      }
    }
    debugAreasApi('PUT /areas/:id request', {
      id,
      payloadKeys: Object.keys(payload),
      mapPatch: Object.keys(mapPatch).length ? mapPatch : undefined,
    });
    try {
      const { data } = await api.put(`/areas/${id}`, payload);
      debugAreasApi('PUT /areas/:id ok', {
        id,
        map: {
          latitude: data.latitude,
          longitude: data.longitude,
          shape: data.shape,
          widthM: data.widthM,
          heightM: data.heightM,
          orientationDegrees: data.orientationDegrees,
        },
      });
      return data;
    } catch (err) {
      debugAreasApi('PUT /areas/:id error', {
        id,
        payloadKeys: Object.keys(payload),
        err: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/areas/${id}`);
  },

  setCurrentPreset: async (id: string, presetId: string, durationMinutes?: number): Promise<SetCurrentPresetResponse> => {
    const { data } = await api.patch(`/areas/${id}/current-preset`, { presetId, durationMinutes });
    return data;
  },
};
